import { useInfiniteQuery } from "@tanstack/react-query";

import { api, BASE_API_URL_V2 } from "@/api/api";
import type {
  HotelsSortValue,
  IHotelsFilters,
} from "@/features/hotels/types/types";
import {
  BOARD_BASIS_OPTIONS,
  HOTEL_CLASSES,
} from "@/features/hotels/utils/constants";
import { formatHotelName } from "@/features/hotels/utils/names";
import type { FindHotelsOutputDtoV2 } from "@/types/codegen";
import { getFilterValue } from "@/utils/filters";

const PAGE_SIZE = 20;

type Params = {
  searchResultKey: string | undefined;
  searchResultToken: string | undefined;
  filters: IHotelsFilters | null;
  bookingNights?: number;
  shouldFetch: boolean;
  sortValue: HotelsSortValue;
  totalOffers?: number;
  destinationCity?: string;
  destinationCountry?: string;
};

export const useHotelSearchResults = ({
  searchResultKey,
  searchResultToken,
  filters,
  shouldFetch,
  sortValue,
  totalOffers,
  destinationCity,
  destinationCountry,
}: Params) => {
  const params = filters
    ? {
        searchResultKey,
        searchResultToken,
        perPage: PAGE_SIZE,
        sortField: sortValue.field,
        sortOrder: sortValue.order,
        minPrice: filters.priceRange[0],
        maxPrice: filters.priceRange[1],
        stars: getFilterValue(filters.starRating, HOTEL_CLASSES),
        boardBasis: getFilterValue(filters.boardBasis, BOARD_BASIS_OPTIONS),
        hotelName:
          (filters.hotelName || "").length >= 2 ? filters.hotelName : undefined,
        destinationCity,
        destinationCountry,
      }
    : null;

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching, isError } =
    useInfiniteQuery({
      queryKey: ["hotels", "search", "results", params] as const,
      queryFn: ({ pageParam, signal }) =>
        api
          .get<FindHotelsOutputDtoV2>("/hotels", {
            baseURL: BASE_API_URL_V2,
            params: {
              ...params,
              page: pageParam,
            },
            signal,
          })
          .then((res) =>
            res.data.data.offers.map((offer) => {
              if (offer.type !== "HOTEL") {
                return offer;
              }

              return {
                ...offer,
                name: formatHotelName(offer.name),
              };
            }),
          ),
      initialPageParam: 1,
      getNextPageParam: (lastPage, pages) => {
        const totalFetched = pages.reduce(
          (sum, page) =>
            sum + page.filter((offer) => offer.type === "HOTEL").length,
          0,
        );

        if (totalOffers !== undefined && totalFetched >= totalOffers) {
          return undefined;
        }

        // Fallback if totalOffers is not provided
        return lastPage.filter((offer) => offer.type === "HOTEL").length ===
          PAGE_SIZE
          ? pages.length + 1
          : undefined;
      },
      enabled: Boolean(
        searchResultKey && searchResultToken && shouldFetch && filters,
      ),
    });

  const isEmpty = data?.pages.flatMap((page) => page).length === 0;
  const hasReachedEnd = !hasNextPage;

  return {
    data,
    isLoading,
    isFetching,
    isError,
    isEmpty,
    hasReachedEnd,
    fetchNextPage,
  };
};
